import axios from "axios";
import React, { useState, useMemo } from "react";

const LOT_SIZES = [
  { id: "small", label: "Small Yard", desc: "Patio / Courtyard (< 500 sq ft)", multiplier: 1 },
  { id: "standard", label: "Standard Yard", desc: "Suburban Scale (500-2K sq ft)", multiplier: 1.5 },
  { id: "large", label: "Large Estate", desc: "Generous Lot (2,000+ sq ft)", multiplier: 2 },
];

const PACKAGES = [
  { id: "soft", title: "Essential Softscaping", basePrice: 100, desc: "Flowerbeds, premium sodding, mulch, and seasonal plant compositions." },
  { id: "hard", title: "Premium Hardscaping", basePrice: 200, desc: "Stone pathways, customized patios, pergolas, and functional masonry paving." },
  { id: "resort", title: "Resort Transformation", basePrice: 300, desc: "Luxury cascading water designs, integrated lighting grids, and elite landscaping features." },
];

const IMAGES = {
  "small-soft": "/images/landscape/plantimages/priceestimateimages/1-basic.jpeg",
  "small-hard": "/images/landscape/plantimages/priceestimateimages/1-medium.jpeg",
  "small-resort": "/images/landscape/plantimages/priceestimateimages/1-premium.jpeg",

  "standard-soft": "/images/landscape/plantimages/priceestimateimages/2-basic.jpeg",
  "standard-hard": "/images/landscape/plantimages/priceestimateimages/2-medium.jpeg",
  "standard-resort": "/images/landscape/plantimages/priceestimateimages/2-premium.jpeg",

  "large-soft": "/images/landscape/plantimages/priceestimateimages/3-basic.jpeg",
  "large-hard": "/images/landscape/plantimages/priceestimateimages/3-medium.jpeg",
  "large-resort": "/images/landscape/plantimages/priceestimateimages/3-premium.jpeg",
};

// Basic phone check: optional leading +, 7-15 digits, spaces/dashes allowed
const PHONE_REGEX = /^\+?[0-9\s-]{7,15}$/;

const Landscape = () => {
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [lotsize, setLotsize] = useState("");
  const [packagetype, setPackagetype] = useState("");

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState(null); // { type: 'success' | 'error', message }

  const selectedcombination = lotsize && packagetype ? `${lotsize}-${packagetype}` : "";

  const selectedLot = LOT_SIZES.find((l) => l.id === lotsize);
  const selectedPackage = PACKAGES.find((p) => p.id === packagetype);

  const estimatedPrice = useMemo(() => {
    if (!selectedLot || !selectedPackage) return null;
    return Math.round(selectedPackage.basePrice * selectedLot.multiplier);
  }, [selectedLot, selectedPackage]);

  const stepsCompleted = [Boolean(lotsize), Boolean(packagetype), Boolean(name.trim() && contact.trim())];

  const validateField = (field, value) => {
    if (field === "name") {
      if (!value.trim()) return "Please enter your name.";
      if (value.trim().length < 2) return "Name looks too short.";
    }
    if (field === "contact") {
      if (!value.trim()) return "Please enter a contact number.";
      if (!PHONE_REGEX.test(value.trim())) return "Enter a valid phone number.";
    }
    return "";
  };

  const handleFieldChange = (field, value) => {
    if (field === "name") setName(value);
    if (field === "contact") setContact(value);
    if (touched[field]) {
      setErrors((prev) => ({ ...prev, [field]: validateField(field, value) }));
    }
  };

  const handleFieldBlur = (field, value) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setErrors((prev) => ({ ...prev, [field]: validateField(field, value) }));
  };

  const handleLotSizeChange = (id) => {
    setLotsize(id);
    setBanner(null);
  };

  const handlePackageTypeChange = (id) => {
    setPackagetype(id);
    setBanner(null);
  };

  const handleCardKeyDown = (e, callback) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      callback();
    }
  };

  const resetForm = () => {
    setName("");
    setContact("");
    setLotsize("");
    setPackagetype("");
    setErrors({});
    setTouched({});
    setBanner(null);
  };

  const submitconsultation = async () => {
    const nameError = validateField("name", name);
    const contactError = validateField("contact", contact);
    setTouched({ name: true, contact: true });
    setErrors({ name: nameError, contact: contactError });

    if (!lotsize || !packagetype) {
      setBanner({ type: "error", message: "Please select a lot size and a design package before submitting." });
      return;
    }
    if (nameError || contactError) {
      setBanner({ type: "error", message: "Please fix the highlighted fields before submitting." });
      return;
    }

    setSubmitting(true);
    setBanner(null);
    try {
      await axios.post("http://localhost:5000/consultation", {
        name,
        contact,
        lotsize,
        packagetype,
      });
      setBanner({ type: "success", message: "Request submitted successfully. We'll be in touch shortly." });
      resetForm();
    } catch (error) {
      console.log(error);
      setBanner({ type: "error", message: "Something went wrong submitting your request. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes shine {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .active-gradient-card {
          background: linear-gradient(120deg, rgba(25, 135, 84, 0.08) 0%, rgba(25, 135, 84, 0.01) 50%, rgba(25, 135, 84, 0.08) 100%) !important;
          background-size: 200% 200% !important;
          animation: shine 4s ease infinite !important;
        }
        .step-card {
          transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .step-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.06);
        }
        .step-card:focus-visible {
          outline: 2px solid #198754;
          outline-offset: 2px;
        }
        .input-animated {
          transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .input-animated:focus {
          background-color: #ffffff !important;
          border-color: #198754 !important;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(25, 135, 84, 0.1) !important;
        }
        .input-invalid {
          border-color: #dc3545 !important;
        }
        .btn-slide-effect {
          transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .btn-slide-effect:hover:not(:disabled) {
          background-color: #157347 !important;
          box-shadow: 0 4px 12px rgba(25, 135, 84, 0.2) !important;
        }
        .btn-slide-effect:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .preview-img {
          transition: transform 0.5s ease;
        }
        .preview-img:hover {
          transform: scale(1.08);
        }
        .banner-fade {
          animation: fadeInUp 0.25s ease;
        }
        .progress-dot {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.7rem;
          font-weight: 700;
          transition: all 0.2s ease;
        }
      `}</style>

      {/* ================= PROJECT ESTIMATOR ====================== */}
      <section
        id="projectEstimator"
        className="py-4"
        style={{
          backgroundColor: "#f8fafc",
          fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        }}
      >
        <div className="wrapper m-3">
          {/* Header Strip */}
          <div className="text-center mb-3">
            <h4 className="fw-bold text-dark mb-1" style={{ letterSpacing: "-0.5px", fontSize: "1.5rem" }}>
              Instant Project Estimator
            </h4>
            <p className="text-muted small mb-0">
              Configure your parameters to dynamically preview your property layout instantly.
            </p>
          </div>

          {/* Progress Indicator */}
          <div className="d-flex justify-content-center align-items-center gap-2 mb-4">
            {["Lot Size", "Package", "Your Info"].map((label, idx) => (
              <React.Fragment key={label}>
                <div className="d-flex align-items-center gap-2">
                  <div
                    className="progress-dot"
                    style={{
                      backgroundColor: stepsCompleted[idx] ? "#198754" : "#e2e8f0",
                      color: stepsCompleted[idx] ? "#fff" : "#64748b",
                    }}
                  >
                    {stepsCompleted[idx] ? "✓" : idx + 1}
                  </div>
                  <span
                    className="small fw-semibold"
                    style={{ color: stepsCompleted[idx] ? "#198754" : "#64748b" }}
                  >
                    {label}
                  </span>
                </div>
                {idx < 2 && (
                  <div style={{ width: "24px", height: "2px", backgroundColor: "#e2e8f0" }} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Main Dashboard Grid */}
          <div className="row g-3 align-items-stretch">
            {/* LEFT SIDE: SELECTION MODULES */}
            <div className="col-md-7">
              <div
                className="p-4 bg-white border border-light-subtle shadow-sm h-100 d-flex flex-column justify-content-between"
                style={{ borderRadius: "14px" }}
              >
                {/* STEP 1: LOT SIZE */}
                <div className="mb-4">
                  <div className="d-flex align-items-center mb-3">
                    <label
                      className="form-label fw-bold text-secondary text-uppercase tracking-wider mb-0"
                      style={{ letterSpacing: "0.5px", fontSize: "0.72rem" }}
                    >
                      Step 1 · Approximate Lot Size
                    </label>
                  </div>

                  <div className="row g-2">
                    {LOT_SIZES.map((item) => {
                      const isSelected = lotsize === item.id;
                      return (
                        <div className="col-md-4" key={item.id}>
                          <div
                            role="button"
                            tabIndex={0}
                            aria-pressed={isSelected}
                            onClick={() => handleLotSizeChange(item.id)}
                            onKeyDown={(e) => handleCardKeyDown(e, () => handleLotSizeChange(item.id))}
                            className={`card step-card h-100 border p-2 position-relative ${
                              isSelected ? "border-success active-gradient-card" : "border-light-subtle bg-white"
                            }`}
                            style={{
                              cursor: "pointer",
                              borderRadius: "10px",
                              transform: isSelected ? "translateY(-2px)" : "none",
                            }}
                          >
                            <div className="card-body p-1">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <h6
                                  className={`fw-bold mb-0 ${isSelected ? "text-success" : "text-dark"}`}
                                  style={{ fontSize: "0.8rem" }}
                                >
                                  {item.label}
                                </h6>
                                {isSelected && (
                                  <span className="text-success fw-bold" style={{ fontSize: "0.75rem" }}>
                                    ●
                                  </span>
                                )}
                              </div>
                              <p className="text-muted mb-0" style={{ fontSize: "0.68rem", lineHeight: "1.3" }}>
                                {item.desc}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* STEP 2: DESIGN PACKAGES */}
                <div className="mb-0">
                  <div className="d-flex align-items-center mb-3">
                    <label
                      className="form-label fw-bold text-secondary text-uppercase tracking-wider mb-0"
                      style={{ letterSpacing: "0.5px", fontSize: "0.72rem" }}
                    >
                      Step 2 · Choose Design Package
                    </label>
                  </div>

                  <div className="d-flex flex-column gap-2">
                    {PACKAGES.map((pkg) => {
                      const isSelected = packagetype === pkg.id;
                      const priceForThisPackage = selectedLot
                        ? Math.round(pkg.basePrice * selectedLot.multiplier)
                        : pkg.basePrice;
                      return (
                        <div
                          key={pkg.id}
                          role="button"
                          tabIndex={0}
                          aria-pressed={isSelected}
                          onClick={() => handlePackageTypeChange(pkg.id)}
                          onKeyDown={(e) => handleCardKeyDown(e, () => handlePackageTypeChange(pkg.id))}
                          className={`card step-card border ${
                            isSelected ? "border-success active-gradient-card" : "border-light-subtle bg-white"
                          }`}
                          style={{ cursor: "pointer", borderRadius: "10px" }}
                        >
                          <div className="card-body p-2 d-flex justify-content-between align-items-center gap-2">
                            <div className="d-flex align-items-center gap-2">
                              <div
                                style={{
                                  width: "11px",
                                  height: "11px",
                                  borderRadius: "50%",
                                  border: isSelected ? "4px solid #198754" : "2px solid #cbd5e1",
                                  backgroundColor: "#fff",
                                  flexShrink: 0,
                                }}
                              />
                              <div>
                                <h6 className="fw-bold text-dark mb-1" style={{ fontSize: "0.85rem" }}>
                                  {pkg.title}
                                </h6>
                                <p
                                  className="text-muted mb-0 d-none d-md-block"
                                  style={{ fontSize: "0.72rem", lineHeight: "1.3" }}
                                >
                                  {pkg.desc}
                                </p>
                              </div>
                            </div>
                            <span
                              className={`badge px-2 py-2 rounded fw-bold ${
                                isSelected ? "bg-success" : "bg-light text-dark border"
                              }`}
                              style={{ fontSize: "0.72rem" }}
                            >
                              AED {priceForThisPackage}
                              {selectedLot && (
                                <span className="d-block fw-normal" style={{ fontSize: "0.6rem", opacity: 0.85 }}>
                                  for {selectedLot.label.toLowerCase()}
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {!selectedLot && (
                    <p className="text-muted mt-2 mb-0" style={{ fontSize: "0.68rem" }}>
                      Pick a lot size above to see pricing tailored to your property.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* RIGHT SIDE: PREVIEW DISPLAY + INTEGRATED FORM SYSTEM */}
            <div className="col-md-5">
              <div
                className="card shadow-sm border border-light-subtle overflow-hidden bg-white h-100 d-flex flex-column justify-content-between"
                style={{ borderRadius: "14px" }}
              >
                {/* Live Preview Canvas Block */}
                <div
                  className="position-relative bg-light d-flex align-items-center justify-content-center flex-grow-1"
                  style={{ minHeight: "200px", overflow: "hidden" }}
                >
                  {selectedcombination && IMAGES[selectedcombination] ? (
                    <img
                      src={IMAGES[selectedcombination]}
                      alt="Landscape Render Layout"
                      className="w-100 h-100 preview-img"
                      style={{ objectFit: "cover", position: "absolute", inset: 0 }}
                    />
                  ) : (
                    <div className="text-center p-4 text-muted">
                      <div className="fs-2 mb-1 opacity-50">📐</div>
                      <h6 className="fw-bold text-dark mb-0" style={{ fontSize: "0.85rem" }}>
                        Interactive Preview
                      </h6>
                      <p className="mb-0 mt-1" style={{ fontSize: "0.7rem" }}>
                        Select a lot size and package to see your render.
                      </p>
                    </div>
                  )}
                </div>

                {/* Micro Metadata Output Strip */}
                <div
                  className="p-2 bg-light bg-opacity-70 border-top border-bottom border-light-subtle d-flex justify-content-around text-center"
                  style={{ fontSize: "0.7rem" }}
                >
                  <div>
                    <span className="text-muted font-monospace">LOT: </span>
                    <strong className="text-dark text-capitalize">{lotsize || "—"}</strong>
                  </div>
                  <div className="border-end border-light-subtle"></div>
                  <div>
                    <span className="text-muted font-monospace">TIER: </span>
                    <strong className="text-dark text-capitalize">{packagetype || "—"}</strong>
                  </div>
                  <div className="border-end border-light-subtle"></div>
                  <div>
                    <span className="text-muted font-monospace">EST: </span>
                    <strong className="text-success">
                      {estimatedPrice !== null ? `AED ${estimatedPrice}` : "—"}
                    </strong>
                  </div>
                </div>

                {/* THE UNIFIED INTEGRATED FORM MODULE */}
                <div className="p-3 bg-white">
                  {banner && (
                    <div
                      className={`banner-fade alert ${
                        banner.type === "success" ? "alert-success" : "alert-danger"
                      } py-2 px-3 mb-2`}
                      style={{ fontSize: "0.75rem", borderRadius: "8px" }}
                      role="alert"
                    >
                      {banner.message}
                    </div>
                  )}

                  <div className="row g-2 mb-1">
                    <div className="col-6">
                      <input
                        type="text"
                        className={`form-control bg-light bg-opacity-50 border border-light-subtle shadow-none py-2 input-animated ${
                          touched.name && errors.name ? "input-invalid" : ""
                        }`}
                        style={{ borderRadius: "8px", fontSize: "0.8rem" }}
                        placeholder="Your Name"
                        value={name}
                        onChange={(e) => handleFieldChange("name", e.target.value)}
                        onBlur={(e) => handleFieldBlur("name", e.target.value)}
                      />
                      {touched.name && errors.name && (
                        <p className="text-danger mb-0 mt-1" style={{ fontSize: "0.65rem" }}>
                          {errors.name}
                        </p>
                      )}
                    </div>
                    <div className="col-6">
                      <input
                        type="text"
                        className={`form-control bg-light bg-opacity-50 border border-light-subtle shadow-none py-2 input-animated ${
                          touched.contact && errors.contact ? "input-invalid" : ""
                        }`}
                        style={{ borderRadius: "8px", fontSize: "0.8rem" }}
                        placeholder="+971 XXXXXXXX"
                        value={contact}
                        onChange={(e) => handleFieldChange("contact", e.target.value)}
                        onBlur={(e) => handleFieldBlur("contact", e.target.value)}
                      />
                      {touched.contact && errors.contact && (
                        <p className="text-danger mb-0 mt-1" style={{ fontSize: "0.65rem" }}>
                          {errors.contact}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="d-flex gap-2 mt-2">
                    <button
                      className="btn btn-success btn-slide-effect flex-grow-1 py-2 fw-semibold border-0 text-white d-flex align-items-center justify-content-center gap-2"
                      style={{ borderRadius: "8px", fontSize: "0.85rem", background: "#198754" }}
                      onClick={submitconsultation}
                      disabled={submitting}
                    >
                      {submitting && (
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        ></span>
                      )}
                      {submitting ? "Submitting..." : "Submit Consultation Request"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary py-2"
                      style={{ borderRadius: "8px", fontSize: "0.8rem" }}
                      onClick={resetForm}
                      disabled={submitting}
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Landscape;